import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SelectInternalTerminal, SelectLocationFull } from '@workspace/db/extra'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import { locationQueryKey, useLocationSelect } from './use-location'

const okEnvelopeSchema = z.object({ success: z.boolean(), message: z.string().optional() }).passthrough()

/** Terminals assigned to the store: `location.terminals` from the shared location read (`withTerminals`). */
export function useLocationTerminals(idOrCode: string) {
  return useLocationSelect(
    idOrCode,
    (loc): SelectInternalTerminal[] => (loc?.terminals ?? []) as SelectInternalTerminal[],
  )
}

// ---- Terminal search ------------------------------------------------------------

const terminalHitSchema = z
  .object({
    id: z.string(),
    name: z.string().nullish(),
    serialNumber: z.string().nullish(),
    model: z.string().nullish(),
    status: z.string().nullish(),
  })
  .passthrough()
const terminalSearchSchema = z.object({
  success: z.boolean(),
  data: z.object({ items: z.array(z.object({ terminals: terminalHitSchema })) }).optional(),
})
export type TerminalSearchHit = z.infer<typeof terminalHitSchema>

/** Pure: v1 drops hits with no name (belt-and-suspenders over the server filter). */
export const namedTerminals = (hits: TerminalSearchHit[]): TerminalSearchHit[] => hits.filter((t) => !!t.name?.trim())

/** Search terminals to assign (GET /v1/terminals?banner&q&pageSize=50). Disabled until `q` is non-empty. */
export function useTerminalSearch(banner: string, q: string) {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'terminal-search', q] : ['terminal-search', banner, q],
    queryFn: async (): Promise<TerminalSearchHit[]> => {
      const params = new URLSearchParams({ banner: banner.toLowerCase(), q, pageSize: '50' })
      const res = await api.get(`/v1/terminals?${params.toString()}`, terminalSearchSchema)
      return namedTerminals(res.data?.items.map((i) => i.terminals) ?? [])
    },
    enabled: !preview && banner.length > 0 && q.trim().length > 0,
    ...(preview ? { initialData: preview.terminalSearch, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

// ---- Printer / cash drawer config -------------------------------------------------

const printerConfigSchema = z.object({
  success: z.boolean(),
  hasPrinter: z.boolean(),
  printerModel: z.string().optional(),
  printerIp: z.string().optional(),
  printerDns: z.string().optional(),
})
const cashDrawerConfigSchema = z.object({
  success: z.boolean(),
  hasCashDrawer: z.boolean(),
  drawerSize: z.enum(['large', 'small', 'micro']).optional(),
  printerType: z.enum(['epson', 'toshiba']).optional(),
  logicalName: z.enum(['CashDrawer1', 'CashDrawer2', 'CashDrawer3', 'CashDrawer4']).optional(),
})
export type PrinterConfig = z.infer<typeof printerConfigSchema>
export type CashDrawerConfig = z.infer<typeof cashDrawerConfigSchema>

const configQs = (code: string, workstationId: string) => new URLSearchParams({ code, workstationId }).toString()

/**
 * Pure: the printer / cash-drawer routes take the JMC business-unit code `<banner>-<aptosStoreCode>` (the handlers
 * `code.split('-')`; a bare store code leaves the location part undefined). A code that already carries the banner
 * is returned as is.
 */
export function businessUnitCode(
  code: string,
  loc?: { banner?: string | null; aptosStoreCode?: string | null } | null,
): string {
  if (code.includes('-') || !loc?.banner) return code
  return `${loc.banner}-${loc.aptosStoreCode || code}`
}

/** Resolve `code` (bare location/aptos code, or already `<banner>-<code>`) from the cached location reads. */
function useBusinessUnitCode() {
  const qc = useQueryClient()
  return (code: string): string => {
    if (code.includes('-')) return code
    const hit = qc
      .getQueriesData<SelectLocationFull | null>({ queryKey: ['location'] })
      .map(([, data]) => data)
      .find((l) => l && (l.code === code || l.aptosStoreCode === code))
    return businessUnitCode(code, hit)
  }
}

/**
 * Receipt-printer config for a workstation (GET /v1/terminals/receipt-printer-config?code&workstationId).
 * `code` is the JMC store code string v1 passes (see the POS terminals screen); `workstationId` from the terminal.
 * Pass `null` workstationId to leave it disabled.
 */
export function usePrinterConfig(code: string, workstationId: string | null) {
  const api = useApi()
  const preview = usePreviewData()
  const resolveCode = useBusinessUnitCode()
  return useQuery({
    queryKey: preview ? ['preview', 'printer-config'] : ['printer-config', code, workstationId],
    queryFn: () =>
      api.get(
        `/v1/terminals/receipt-printer-config?${configQs(resolveCode(code), workstationId ?? '')}`,
        printerConfigSchema,
      ),
    enabled: !preview && workstationId !== null && code.length > 0,
    ...(preview ? { initialData: preview.printerConfig, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

/** Cash-drawer config (GET /v1/terminals/cash-drawer-config?code&workstationId). Same args as `usePrinterConfig`. */
export function useCashDrawerConfig(code: string, workstationId: string | null) {
  const api = useApi()
  const preview = usePreviewData()
  const resolveCode = useBusinessUnitCode()
  return useQuery({
    queryKey: preview ? ['preview', 'cash-drawer-config'] : ['cash-drawer-config', code, workstationId],
    queryFn: () =>
      api.get(
        `/v1/terminals/cash-drawer-config?${configQs(resolveCode(code), workstationId ?? '')}`,
        cashDrawerConfigSchema,
      ),
    enabled: !preview && workstationId !== null && code.length > 0,
    ...(preview ? { initialData: preview.cashDrawerConfig, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

// ---- Mutations -------------------------------------------------------------------

const workstationNumbersSchema = z.object({
  success: z.boolean(),
  workstationNumbers: z.record(z.string(), z.string().nullable()).optional(),
})

/** Read-ish lookup (POST /v1/terminals/jmc-workstation-numbers). `mutate({ terminalNames, banner })` -> `{ workstationNumbers }`. */
export function useJmcWorkstationNumbers() {
  const api = useApi()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: { terminalNames: string[]; banner: string }) => {
      if (preview) return { success: true, workstationNumbers: {} as Record<string, string | null> }
      return api.request('/v1/terminals/jmc-workstation-numbers', { method: 'POST', body: v }, workstationNumbersSchema)
    },
  })
}

/**
 * All POS writes are workflow-async (v1 applies a 90s per-workstation+component cooldown in KV; that lock is
 * DEFERRED, designs should use a client timer). Each mutation invalidates the location (terminals) plus the
 * printer/drawer config queries.
 */
function useInvalidatePos() {
  const qc = useQueryClient()
  return async (idOrCode?: string) => {
    await Promise.all([
      idOrCode
        ? qc.invalidateQueries({ queryKey: locationQueryKey(idOrCode) })
        : qc.invalidateQueries({ queryKey: ['location'] }),
      qc.invalidateQueries({ queryKey: ['printer-config'] }),
      qc.invalidateQueries({ queryKey: ['cash-drawer-config'] }),
    ])
  }
}

/** Assign a terminal to the store (POST /v1/terminals/assign `{terminalId, storeId}`). `mutate({ idOrCode, terminalId, storeCode })`. */
export function useAssignTerminal() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidatePos()
  return useMutation({
    mutationFn: async (v: { idOrCode: string; terminalId: string; storeCode: string }) => {
      if (preview) return { success: true }
      return api.request(
        '/v1/terminals/assign',
        { method: 'POST', body: { terminalId: v.terminalId, storeId: v.storeCode } },
        okEnvelopeSchema,
      )
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode)),
  })
}

/** Unassign a terminal (DELETE /v1/terminals/unassign `{terminalId}`). `mutate({ idOrCode, terminalId })`. */
export function useUnassignTerminal() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidatePos()
  return useMutation({
    mutationFn: async (v: { idOrCode: string; terminalId: string }) => {
      if (preview) return { success: true }
      return api.request(
        '/v1/terminals/unassign',
        { method: 'DELETE', body: { terminalId: v.terminalId } },
        okEnvelopeSchema,
      )
    },
    onSuccess: (_r, v) => (preview ? undefined : invalidate(v.idOrCode)),
  })
}

/** Set up a receipt printer (POST /v1/terminals/setup-receipt-printer). */
export function useSetupReceiptPrinter() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidatePos()
  const resolveCode = useBusinessUnitCode()
  return useMutation({
    mutationFn: async (v: {
      code: string
      workstationId: string
      printerModel: string
      printerMac?: string
      /** Toshiba6145 serial printers only (API default COM3). */
      serialPort?: 'COM2' | 'COM3' | 'COM4'
    }) => {
      if (preview) return { success: true }
      return api.request(
        '/v1/terminals/setup-receipt-printer',
        { method: 'POST', body: { ...v, code: resolveCode(v.code), printerMac: v.printerMac || undefined } },
        okEnvelopeSchema,
      )
    },
    onSuccess: () => (preview ? undefined : invalidate()),
  })
}

/** Unassign the receipt printer (DELETE /v1/terminals/unassign-receipt-printer `{code, workstationId}`). */
export function useUnassignReceiptPrinter() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidatePos()
  const resolveCode = useBusinessUnitCode()
  return useMutation({
    mutationFn: async (v: { code: string; workstationId: string }) => {
      if (preview) return { success: true }
      return api.request(
        '/v1/terminals/unassign-receipt-printer',
        { method: 'DELETE', body: { ...v, code: resolveCode(v.code) } },
        okEnvelopeSchema,
      )
    },
    onSuccess: () => (preview ? undefined : invalidate()),
  })
}

/** Pure: v1 rule, a large drawer requires a printer type. Returns an error string or null. */
export function cashDrawerError(v: { drawerSize: 'large' | 'small'; printerType?: string }): string | null {
  return v.drawerSize === 'large' && !v.printerType ? 'Printer type required for large cash drawer' : null
}

/** Set up a cash drawer (POST /v1/terminals/setup-cash-drawer). Validate with `cashDrawerError` first. */
export function useSetupCashDrawer() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidatePos()
  const resolveCode = useBusinessUnitCode()
  return useMutation({
    mutationFn: async (v: {
      code: string
      workstationId: string
      drawerSize: 'large' | 'small' | 'micro'
      printerType?: 'epson' | 'toshiba'
      logicalName?: 'CashDrawer1' | 'CashDrawer2' | 'CashDrawer3' | 'CashDrawer4'
    }) => {
      if (preview) return { success: true }
      return api.request(
        '/v1/terminals/setup-cash-drawer',
        {
          method: 'POST',
          body: {
            ...v,
            code: resolveCode(v.code),
            printerType: v.printerType || undefined,
            logicalName: v.logicalName || undefined,
          },
        },
        okEnvelopeSchema,
      )
    },
    onSuccess: () => (preview ? undefined : invalidate()),
  })
}

/** Unassign the cash drawer (DELETE /v1/terminals/unassign-cash-drawer `{code, workstationId}`). */
export function useUnassignCashDrawer() {
  const api = useApi()
  const preview = usePreviewData()
  const invalidate = useInvalidatePos()
  const resolveCode = useBusinessUnitCode()
  return useMutation({
    mutationFn: async (v: { code: string; workstationId: string }) => {
      if (preview) return { success: true }
      return api.request(
        '/v1/terminals/unassign-cash-drawer',
        { method: 'DELETE', body: { ...v, code: resolveCode(v.code) } },
        okEnvelopeSchema,
      )
    },
    onSuccess: () => (preview ? undefined : invalidate()),
  })
}
